import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronRight } from "lucide-react";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_VIDEO = "/assets/hero-video.mp4";
const HERO_POSTER = "/assets/projects/chi-chi-mar/01_dsc02713.jpg";

const LIVE_OUTSIDE_VIDEO = "/assets/live-outside-video.mp4";
const LIVE_OUTSIDE_POSTER = "/assets/live-outside-poster.jpg";

const TEXTURE_IMG = "/assets/projects/casa-roca/img_20231127_114801.jpg";

const PORTFOLIO_IMAGES = [
  "/assets/projects/kitchen/01_dsc03664.jpg",
  "/assets/projects/clements/01_render.jpg",
  "/assets/projects/jardin-nacho/01_dsc00535.jpg"
];

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Home() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API}/projects?featured=true`);
        if (response.ok) {
          const data = await response.json();
          setProjects(data.slice(0, 3));
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div data-testid="home-page">
      {/* ===== HERO (stays dark - video bg) ===== */}
      <section className="relative min-h-screen flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <video
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay loop muted playsInline preload="auto"
            className="w-full h-full object-cover"
            onPlaying={() => setVideoReady(true)}
          />
        </div>
        <div
          className="absolute inset-0 bg-[#1C2E24] transition-opacity duration-[1500ms] ease-out pointer-events-none"
          style={{ opacity: videoReady ? 0 : 1 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C2E24]/70 via-black/20 to-black/10" />

        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="max-w-3xl"
          >
            <span className="inline-block text-xs tracking-[0.3em] uppercase text-white/70 mb-6 font-sans font-semibold">
              {t('hero.subtitle')}
            </span>
            <h1 className="text-5xl md:text-6xl font-serif font-light tracking-tight text-white mb-8 leading-[1.1]">
              {t('hero.title')}
            </h1>
            <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-xl mb-10 font-sans">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex flex-col">
                <Link to="/contacto" className="btn-hero-primary" data-testid="hero-cta-primary">
                  {t('hero.ctaPrimary')}
                </Link>
                <span className="text-[10px] text-white/40 mt-2 tracking-wide font-sans">{t('hero.ctaPrimaryNote')}</span>
              </div>
              <Link to="/portfolio" className="btn-hero-secondary self-start" data-testid="hero-cta-secondary">
                {t('hero.ctaSecondary')}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            className="absolute bottom-8 right-12 hidden md:flex flex-col items-center gap-3"
          >
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-sans" style={{ writingMode: 'vertical-lr' }}>
              scroll
            </span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-[1px] h-12 bg-gradient-to-b from-white/40 to-transparent"
            />
          </motion.div>
        </div>
      </section>

      {/* ===== MARQUEE RIBBON ===== */}
      <div className="marquee-ribbon" data-testid="marquee-ribbon">
        <Marquee speed={40} gradient={false}>
          {[...Array(6)].map((_, i) => (
            <span key={i} className="marquee-text mx-12">
              Live Outside
              <span className="mx-8 opacity-30">/</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* ===== PHILOSOPHY ===== */}
      <section className="py-32 md:py-40 bg-[#F9F6F0]" data-testid="philosophy-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <FadeIn className="md:col-span-5 order-2 md:order-1">
              <span className="text-xs tracking-[0.3em] uppercase text-[#547055] mb-6 block font-sans font-semibold">
                {t('philosophy.subtitle')}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight leading-[1.15] mb-8">
                {t('philosophy.title')}
              </h2>
              <p className="text-base text-[#2A382C] leading-relaxed mb-10 font-sans">
                {t('philosophy.paragraph')}
              </p>
              <blockquote className="border-l-2 border-[#849C8A] pl-6 py-4">
                <p className="text-sm text-[#547055] leading-relaxed italic mb-3 font-sans">
                  {t('philosophy.quote')}
                </p>
                <cite className="text-xs text-[#849C8A] not-italic tracking-wider font-sans">
                  {t('philosophy.quoteAuthor')}
                </cite>
              </blockquote>
            </FadeIn>

            <FadeIn delay={0.15} className="md:col-span-7 order-1 md:order-2">
              <div className="aspect-[4/3] md:aspect-[16/10] overflow-hidden relative rounded-sm">
                <video
                  autoPlay loop muted playsInline preload="none"
                  poster={LIVE_OUTSIDE_POSTER}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '70% center', transform: 'scale(1.15)' }}
                  onLoadedMetadata={(e) => { e.target.playbackRate = 0.75; }}
                  onTimeUpdate={(e) => {
                    if (e.target.duration && e.target.currentTime > e.target.duration - 3) {
                      e.target.currentTime = 0;
                    }
                  }}
                >
                  <source src={LIVE_OUTSIDE_VIDEO} type="video/mp4" />
                </video>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <FadeIn>
        <div className="divider-sand" />
        <div className="py-16 md:py-20 bg-[#EFEAD8]" data-testid="stats-section">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {[
                { num: "50+", label: t('stats.projects') },
                { num: "8", label: t('stats.years') },
                { num: "100%", label: t('stats.native') },
                { num: "BCS", label: t('stats.location') }
              ].map((stat, i) => (
                <div key={i} className="text-center md:text-left">
                  <span className="stat-number text-4xl md:text-5xl block mb-2">{stat.num}</span>
                  <span className="text-xs tracking-[0.15em] uppercase text-[#547055] font-sans">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="divider-sand" />
      </FadeIn>

      {/* ===== SERVICES ===== */}
      <section className="py-32 md:py-40 bg-[#F9F6F0]" data-testid="services-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
              <div>
                <span className="text-xs tracking-[0.3em] uppercase text-[#547055] mb-4 block font-sans font-semibold">
                  {t('services.subtitle')}
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight">
                  {t('services.title')}
                </h2>
              </div>
              <Link to="/servicios" className="flex items-center gap-2 text-sm text-[#547055] hover:text-[#1C2E24] transition-colors font-sans">
                {t('services.viewAll')}
                <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {[
              { titleKey: 'services.service1.title', descKey: 'services.service1.description', link: "/servicios#diseno", span: "md:col-span-5" },
              { titleKey: 'services.service2.title', descKey: 'services.service2.description', link: "/servicios#proyectos", span: "md:col-span-4" },
              { titleKey: 'services.service3.title', descKey: 'services.service3.description', link: "/servicios#mantenimiento", span: "md:col-span-3" }
            ].map((service, index) => (
              <FadeIn key={service.titleKey} delay={index * 0.1} className={service.span}>
                <Link to={service.link} className="service-card group h-full" data-testid={`service-card-${index}`}>
                  <div className="flex-grow">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#849C8A] block mb-4 font-sans">
                      0{index + 1}
                    </span>
                    <h3 className="text-2xl font-serif font-light mb-4 group-hover:text-[#547055] transition-colors duration-500">
                      {t(service.titleKey)}
                    </h3>
                    <p className="text-sm text-[#547055] leading-relaxed font-sans">
                      {t(service.descKey)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#849C8A] mt-8 group-hover:text-[#547055] group-hover:gap-4 transition-all font-sans">
                    {t('services.learnMore')}
                    <ChevronRight size={14} />
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PORTFOLIO - TETRIS GRID ===== */}
      <section className="py-32 md:py-40 bg-[#EFEAD8]" data-testid="portfolio-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
              <div>
                <span className="text-xs tracking-[0.3em] uppercase text-[#547055] mb-4 block font-sans font-semibold">
                  {t('portfolio.subtitle')}
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight">
                  {t('portfolio.title')}
                </h2>
              </div>
              <Link to="/portfolio" className="flex items-center gap-2 text-sm text-[#547055] hover:text-[#1C2E24] transition-colors font-sans">
                {t('portfolio.viewAll')}
                <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>

          {projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <FadeIn className="md:col-span-8">
                <Link to={`/portfolio/${projects[0].id}`} className="group block" data-testid="featured-project">
                  <div className="relative aspect-[16/10] overflow-hidden mb-4 rounded-sm">
                    <img src={projects[0].images?.[0] || PORTFOLIO_IMAGES[0]} alt={projects[0].title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <span className="tag mb-2">{projects[0].category?.replace("_", " ")}</span>
                  <h3 className="text-2xl md:text-3xl font-serif font-light mb-1 group-hover:text-[#547055] transition-colors duration-500">
                    {projects[0].title}
                  </h3>
                  <p className="text-sm text-[#547055] font-sans">{projects[0].location} — {projects[0].year}</p>
                </Link>
              </FadeIn>
              <div className="md:col-span-4 flex flex-col gap-6">
                {projects.slice(1, 3).map((project, index) => (
                  <FadeIn key={project.id} delay={(index + 1) * 0.15}>
                    <Link to={`/portfolio/${project.id}`} className="group block" data-testid={`project-card-${project.id}`}>
                      <div className="relative aspect-[4/3] overflow-hidden mb-3 rounded-sm">
                        <img src={project.images?.[0] || PORTFOLIO_IMAGES[(index + 1) % 3]} alt={project.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                      <span className="tag mb-1">{project.category?.replace("_", " ")}</span>
                      <h3 className="text-lg font-serif font-light group-hover:text-[#547055] transition-colors duration-500">{project.title}</h3>
                      <p className="text-xs text-[#547055] font-sans">{project.location} — {project.year}</p>
                    </Link>
                  </FadeIn>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <FadeIn className="md:col-span-8">
                <div className="relative aspect-[16/10] overflow-hidden rounded-sm">
                  <img src={PORTFOLIO_IMAGES[0]} alt="Portfolio" className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm text-[#547055] uppercase tracking-wider font-sans">{t('portfolio.noProjects')}</span>
                  </div>
                </div>
              </FadeIn>
              <div className="md:col-span-4 flex flex-col gap-6">
                {PORTFOLIO_IMAGES.slice(1, 3).map((img, i) => (
                  <FadeIn key={i} delay={(i + 1) * 0.15}>
                    <div className="relative aspect-[4/3] overflow-hidden rounded-sm">
                      <img src={img} alt="" className="w-full h-full object-cover opacity-60" />
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== TEXTURE + PHILOSOPHY BREAK ===== */}
      <section className="relative py-32 md:py-40 overflow-hidden" data-testid="texture-break">
        <div className="absolute inset-0">
          <img src={TEXTURE_IMG} alt="" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C2E24]/60" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.3em] uppercase text-white/70 mb-6 block font-sans font-semibold">
                {t('philosophy.subtitle')}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight leading-[1.2] text-white mb-8">
                {t('texture.headline')}
              </h2>
              <p className="text-base text-white/70 leading-relaxed mb-10 max-w-xl font-sans">
                {t('texture.paragraph')}
              </p>
              <Link to="/servicios" className="btn-hero-primary inline-flex items-center gap-2" data-testid="texture-cta">
                {t('texture.cta')}
                <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-32 md:py-40 bg-[#1C2E24] relative overflow-hidden" data-testid="cta-section">
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
              <div className="md:col-span-7">
                <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-white mb-6 leading-[1.15]">
                  {t('cta.title')}
                </h2>
                <p className="text-base text-white/60 max-w-xl mb-10 font-sans">
                  {t('cta.description')}
                </p>
                <div className="flex flex-col">
                  <Link to="/contacto" className="btn-hero-primary inline-block" data-testid="cta-button">
                    {t('cta.button')}
                  </Link>
                  <span className="text-[10px] text-white/30 mt-2 tracking-wide font-sans">{t('cta.buttonNote')}</span>
                </div>
              </div>
              <div className="md:col-span-5 hidden md:block">
                <div className="text-right">
                  <span className="stat-number text-[8rem] leading-none text-white/5">BCS</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== TESTIMONIAL ===== */}
      <section className="py-32 md:py-40 bg-[#F9F6F0]" data-testid="testimonial-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="max-w-4xl">
              <span className="text-7xl md:text-8xl font-serif text-[#849C8A]/20 block mb-6 leading-none">"</span>
              <blockquote className="text-2xl md:text-3xl font-serif font-light leading-relaxed mb-8 text-[#1C2E24] tracking-tight">
                {t('testimonial.quote')}
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-[1px] bg-[#849C8A]" />
                <span className="text-xs text-[#547055] tracking-[0.15em] uppercase font-sans">
                  Circle / Natural Editing
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

    </div>
  );
}
